export function TimingYourSale() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Timing Your Sale for Maximum Value
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Timing is one of the most underrated tips for selling an apartment building. Market conditions, 
            seasonal factors, and economic cycles all impact buyer demand and pricing. Understanding when to 
            sell can mean the difference between a good sale and an exceptional one.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Market Cycles</h3>
            <p className="text-gray-600 mb-6">
              Real estate markets move in cycles, and recognizing where your market stands in the cycle is 
              crucial for timing your sale. Selling during peak market conditions can add 10-20% or more to 
              your sale price compared to selling during market downturns. Monitor these key indicators to 
              identify optimal selling windows.
            </p>
            <div className="space-y-4">
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-arrow-up-line text-green-600 mr-2"></i>
                  Expansion Phase (Best Time to Sell)
                </h4>
                <p className="text-gray-600 text-sm mb-2">
                  Characterized by rising rents, declining vacancy rates, increasing property values, and 
                  strong buyer demand. Cap rates compress as investors compete for properties.
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Indicators:</strong> Job growth, population increases, new business development, 
                  rising rental rates, multiple offers on properties
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-arrow-right-line text-orange-600 mr-2"></i>
                  Peak Phase (Good Time to Sell)
                </h4>
                <p className="text-gray-600 text-sm mb-2">
                  Market reaches maximum values with high prices and strong demand, but growth begins to slow. 
                  Smart sellers recognize peak conditions and list before the market turns.
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Indicators:</strong> Slowing rent growth, increased new construction, rising cap rates, 
                  longer time on market
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-red-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-arrow-down-line text-red-600 mr-2"></i>
                  Contraction Phase (Consider Waiting)
                </h4>
                <p className="text-gray-600 text-sm mb-2">
                  Declining rents, rising vacancies, falling property values, and reduced buyer activity. 
                  Unless you must sell, waiting for market recovery typically yields better results.
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Indicators:</strong> Job losses, business closures, rising vacancy rates, declining 
                  rents, fewer buyers
                </p>
              </div>
              <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-indigo-500">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <i className="ri-arrow-left-line text-indigo-600 mr-2"></i>
                  Recovery Phase (Opportunity for Value Buyers)
                </h4>
                <p className="text-gray-600 text-sm mb-2">
                  Market begins improving with stabilizing occupancy and modest rent growth. Buyers return 
                  but remain cautious. Good time to sell if you bought during contraction.
                </p>
                <p className="text-xs text-gray-500">
                  <strong>Indicators:</strong> Stabilizing vacancy, modest rent increases, returning buyer 
                  confidence, economic improvement
                </p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/379e257c5e39f1b4161477ac5529099a.jpg"
              alt="Market cycle analysis"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Seasonal Considerations for Apartment Building Sales
          </h3>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            While apartment buildings can sell year-round, certain seasons typically see more buyer activity 
            and better pricing. Understanding seasonal patterns is among the practical tips for selling an 
            apartment building that can improve your results with minimal effort.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-leaf-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Spring (March-May)</h4>
              <p className="text-gray-600 text-sm mb-3">
                <strong className="text-green-600">Peak Season</strong> - Highest buyer activity and best pricing
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Maximum buyer activity</li>
                <li>• Properties show well with landscaping</li>
                <li>• Investors planning for summer acquisitions</li>
                <li>• Tax refunds provide down payment funds</li>
                <li>• Ideal weather for property tours</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-sun-line text-2xl text-amber-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Summer (June-August)</h4>
              <p className="text-gray-600 text-sm mb-3">
                <strong className="text-amber-600">Good Season</strong> - Strong activity but some vacation slowdown
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Continued strong buyer interest</li>
                <li>• Properties at peak appearance</li>
                <li>• Some slowdown during vacation periods</li>
                <li>• Good for showing outdoor amenities</li>
                <li>• Longer daylight for evening showings</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-contrast-drop-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Fall (September-November)</h4>
              <p className="text-gray-600 text-sm mb-3">
                <strong className="text-orange-600">Peak Season</strong> - Second-best time with motivated buyers
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Serious buyers back from summer</li>
                <li>• Investors seeking year-end acquisitions</li>
                <li>• 1031 exchange deadline pressure</li>
                <li>• Pleasant weather for inspections</li>
                <li>• Less competition from new listings</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-snowy-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Winter (December-February)</h4>
              <p className="text-gray-600 text-sm mb-3">
                <strong className="text-blue-600">Slow Season</strong> - Fewer buyers but less competition
              </p>
              <ul className="text-gray-600 text-xs space-y-2">
                <li>• Reduced buyer activity</li>
                <li>• Holiday distractions</li>
                <li>• Serious buyers only (less competition)</li>
                <li>• Properties may show poorly in cold climates</li>
                <li>• Potential for motivated year-end buyers</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=real%20estate%20investor%20analyzing%20economic%20indicators%20interest%20rates%20inflation%20data%20financial%20news%20on%20computer%20screens%20monitoring%20market%20conditions%20for%20apartment%20building%20investment%20timing%20professional%20trader%20workspace&width=800&height=600&seq=tips-timing-002&orientation=landscape"
              alt="Economic indicators analysis"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Economic Factors to Monitor</h3>
            <p className="text-gray-600 mb-6">
              Broader economic conditions significantly impact apartment building values and buyer demand. 
              Monitoring these economic indicators helps you identify optimal selling windows and is among 
              the most sophisticated tips for selling an apartment building at peak value.
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-percent-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Interest Rates</h4>
                  <p className="text-gray-600 text-sm">
                    Low interest rates increase buyer purchasing power and property values. Consider selling 
                    before anticipated rate increases, as rising rates reduce buyer demand and compress values. 
                    Monitor Federal Reserve policy and mortgage rate trends.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-briefcase-line text-orange-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Employment Trends</h4>
                  <p className="text-gray-600 text-sm">
                    Strong job growth drives rental demand and property values. Markets with diverse, growing 
                    employment bases attract more buyers and command premium prices. Sell when employment trends 
                    are positive and before potential economic slowdowns.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-home-heart-line text-red-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Housing Supply</h4>
                  <p className="text-gray-600 text-sm">
                    Limited housing supply increases rental demand and property values. Monitor new construction 
                    pipelines - sell before significant new supply hits the market. Markets with constrained 
                    supply due to geography or regulations maintain stronger values.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-line-chart-line text-indigo-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Cap Rate Trends</h4>
                  <p className="text-gray-600 text-sm">
                    Declining cap rates indicate increasing property values and strong buyer demand. Sell when 
                    cap rates are at or near historic lows. Rising cap rates signal weakening markets and 
                    declining values - consider accelerating your sale timeline.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-government-line text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Government Policy</h4>
                  <p className="text-gray-600 text-sm">
                    Tax policy changes, zoning regulations, and rent control legislation impact property values. 
                    Sell before unfavorable policy changes take effect. Conversely, favorable policy changes 
                    like opportunity zones can create selling opportunities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Personal Timing Considerations
          </h3>
          <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
            While market timing is important, your personal situation and investment goals should drive the 
            ultimate decision. These personal factors are equally important tips for selling an apartment 
            building at the right time for your circumstances.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-trophy-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Investment Goals Achieved</h4>
              <p className="text-gray-600 text-sm">
                If you've reached your target return on investment, appreciation goals, or cash flow objectives, 
                it may be time to sell regardless of market conditions. Don't let greed prevent you from 
                realizing excellent returns. Lock in profits and redeploy capital into new opportunities.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-orange-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Major Capital Needs</h4>
              <p className="text-gray-600 text-sm">
                If your property requires significant capital expenditures (roof replacement, major systems 
                upgrades, etc.), consider selling before these expenses arise. Buyers will discount heavily 
                for deferred maintenance, so selling before major repairs are needed often yields better net 
                proceeds.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Life Changes</h4>
              <p className="text-gray-600 text-sm">
                Retirement, health issues, partnership disputes, or other life changes may necessitate selling. 
                While you should still consider market timing, don't let less-than-perfect market conditions 
                prevent necessary sales. Work with professionals to maximize value within your timeline constraints.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}